import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Flame, ShieldCheck } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import RoleSwitcher from "../common/RoleSwitcher";
import NotificationBell from "../common/NotificationBell";

export default function TopBar({ title, back = false, showProfile = true, right, hideMenu = false }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const initial = (user?.name || user?.email || "F")[0]?.toUpperCase();
  const showRightChrome = !hideMenu;
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    if (back || !user) return;
    apiClient
      .get("/ai-trainer/streak")
      .then((r) => setStreak(r.data?.streak_days || 0))
      .catch(() => setStreak(0));
  }, [back, user]);

  return (
    <header
      data-testid="top-bar"
      className="sticky top-0 z-20 bg-canvas/85 backdrop-blur-xl"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="max-w-2xl mx-auto h-14 px-4 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {back ? (
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 -ml-2 grid place-items-center rounded-full hover:bg-white/60 transition-colors"
              data-testid="top-bar-back"
              aria-label="Back"
            >
              <ChevronLeft size={22} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate(user ? "/home" : "/")}
              className="flex items-center gap-2"
              data-testid="top-bar-brand"
              aria-label={user ? "Go to home" : "FitFrik"}
              style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
            >
              <span
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 800,
                  fontSize: 26,
                  letterSpacing: "-1px",
                  lineHeight: 1,
                  display: "inline-flex",
                  alignItems: "baseline",
                }}
              >
                <span style={{ color: "#00D4FF" }}>F</span>
                <span style={{ color: "#1A1A1A" }}>itFrik</span>
              </span>
            </button>
          )}
          {title ? (
            <h1 className="font-display text-[18px] sm:text-[20px] truncate ml-1">{title}</h1>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {streak >= 3 && !back && (
            <button
              type="button"
              onClick={() => navigate("/ai-trainer/coach")}
              data-testid="ai-trainer-streak-badge"
              aria-label={`AI Trainer streak ${streak} days`}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-pill"
              style={{
                background: "rgba(255, 122, 0, 0.12)",
                color: "#FF7A00",
                borderRadius: 100,
              }}
            >
              <Flame size={13} fill="#FF7A00" />
              <span style={{ fontFamily: "Barlow, sans-serif", fontSize: 12, fontWeight: 700 }}>
                {streak}
              </span>
            </button>
          )}
          {right}
          {showRightChrome && <RoleSwitcher variant="light" />}
          {showRightChrome && user?.is_superadmin && (
            <button
              type="button"
              onClick={() => navigate("/superadmin")}
              data-testid="superadmin-link"
              aria-label="Open Super Admin console"
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-pill"
              style={{
                background: "rgba(245, 197, 24, 0.18)",
                color: "#0C0C0B",
                borderRadius: 100,
                border: "1px solid rgba(245, 197, 24, 0.45)",
              }}
            >
              <ShieldCheck size={13} />
              <span style={{ fontFamily: "Barlow, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                Super Admin
              </span>
            </button>
          )}
          {showRightChrome && (
            <NotificationBell />
          )}
          {showRightChrome && showProfile && (
            <button
              data-testid="profile-btn"
              onClick={() => navigate("/settings")}
              className="w-10 h-10 grid place-items-center rounded-full overflow-hidden border border-line"
              aria-label="Profile"
              style={{ background: "var(--bg-secondary)" }}
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-semibold">{initial}</span>
              )}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
